// Importing React to build the component.
import React from 'react';

// Importing the Navbar component.
import NavBar from './Navbar';

// Importing the CSS file for styling the help page.
import './HelpPage.css';

// Importing the useState hook.
import { useState } from "react";

// Importing the Bootstrap Collapse and Container components.
import Collapse from 'react-bootstrap/Collapse';
import Container from 'react-bootstrap/Container';

function HelpPage() {
    // Set the initial state of the sections to closed.
    const [openSections, setOpenSections] = useState([false, false, false, false, false]);

    // Function to open/close the sections.
    const changeSectionStates = (index) => {
        const newOpenSections = [...openSections];
        newOpenSections[index] = !newOpenSections[index];
        setOpenSections(newOpenSections);
    };

    return (
        <div>
            {/* Render the Navbar component. */}
            <NavBar />
            <div className="app-page-shell">
            <Container className='help-page-content'>
                <h1>Help Page</h1>
                <p>Welcome to the Help page! This page contains helpful information about the following topics:</p>
                <ul>
                    <li>How to Use the AI Tutor Website</li>
                    <li>Academic Integrity</li>
                    <li>How to Write a Good Prompt</li>
                    <li>Common AI Tutor Responses</li>
                    <li>FAQs</li>
                </ul>
                <h3 className="h3-help" onClick={() => changeSectionStates(0)}>How to Use the AI Tutor Website</h3>
                <hr />
                <Collapse in={openSections[0]}>
                    <div>
                        <h4>Chat Page Features</h4>
                        <p>The Chat page contains the following features:</p>
                        <ul>
                            <li>On the sidebar, you can choose the class that you want to use the AI Tutor for. When you select a class, your chat history for that class will appear in the sidebar. You can view an old chat by clicking on it.</li>
                            <li>You can close the sidebar by pressing the <strong>&lt;</strong> button.</li>
                            <li>Select a class in the sidebar and start typing. A chat is only saved after you send your first real message for that course.</li>
                            <li>You can interact with the AI Tutor by typing your prompt into the textbox at the bottom of the screen that says "Type your prompt here..." and then clicking the arrow button to send your prompt to the AI Tutor. (You can refer to the How to Write a Good Prompt section below to get useful tips on how to have a beneficial interaction with the AI Tutor!)</li>
                            <li>You can navigate to the Chat page by clicking on the <strong>Chat</strong> button in the header menu.</li>
                        </ul>
                        <h4>Feedback Page Features</h4>
                        <p>The Feedback page contains the following features:</p>
                        <ul>
                            <li>You can leave feedback about your experience using the AI Tutor Website by writing your feedback in the textbox that says "Write your feedback here..." and then clicking the <strong>Submit Feedback</strong> button.</li>
                            <li>You can navigate to the Feedback page by clicking on the <strong>Feedback</strong> button in the header menu.</li>
                        </ul>
                        <h4>Profile Page Features</h4>
                        <p>The Profile page contains the following features:</p>
                        <ul>
                            <li>In the Profile box, you can view your personal information, including your first name, last name, unique ID, major, and year.</li>
                            <li>In the Update Year and Major box, you can update your year and major by clicking the <strong>Update</strong> button.</li>
                            <li>You can navigate to the Profile page by clicking on the <strong>Profile</strong> drop down button in the header menu and then clicking on the <strong>Profile</strong> button.</li>
                        </ul>
                        <h4>Other Features</h4>
                        <p>Other features of the AI Tutor website include the following:</p>
                        <ul>
                            <li>You can switch between light mode and dark mode by clicking the <strong>Dark Mode</strong> switch button in the header menu.</li>
                            <li>You can logout of the AI Tutor website by clicking on the <strong>Profile</strong> drop down button in the header menu and then clicking on the <strong>Logout</strong> button.</li>
                            <li>You can change the font size by clicking the <strong>Font Size</strong> drop down button in the header menu.</li>
                        </ul>
                    </div>
                </Collapse>
                <h3 className="h3-help" onClick={() => changeSectionStates(1)}>Academic Integrity</h3>
                <hr />
                <Collapse in={openSections[1]}>
                    <div>
                        <p>The academic integrity disclaimer that you must accept in order to interact with the AI Tutor is as follows:</p>
                        <Container>
                            <Container>
                                <Container>
                                    <p><i>I understand that using any AI tool in any phase of an assignment without permission from the course instructor is a violation of Miami's academic integrity policy. I also understand that use of the AI Tutor tool is for the purposes of aiding learning, and that I may not use the tool to complete academic work in an unauthorized manner. I understand that if I use the AI Tutor tool in a manner not authorized by my instructors that I will be charged with a violation of the Academic Integrity Policy.</i></p>
                                    <p><i>I attest that I have received express permission from my instructors to use the AI Tutor tool and that I will not use it in an unauthorized manner.</i></p>
                                </Container>
                            </Container>
                        </Container>
                        <p>The AI Tutor is designed to prevent both intentional and unintentional academic integrity violations. However, the following is advised in case of error on the part of the AI Tutor:</p>
                        <ul>
                            <li>Do not ask the AI Tutor for answers to assignments or assessments.</li>
                            <li>Do not use answers provided by the AI Tutor for assignments or assessments.</li>
                            <li>Make sure you have permission from your instructor before using the AI Tutor.</li>
                        </ul>
                        <p>Please note that your chat history with the AI Tutor is not confidential, and it may be used in cases where an academic integrity violation is suspected. If you have any questions about academic integrity in regards to the AI Tutor, please contact Miami University's Office of Undergraduate Education at <strong>academicintegrity@MiamiOH.edu</strong> or <strong>513-529-0027</strong>.</p>
                    </div>
                </Collapse>
                <h3 className="h3-help" onClick={() => changeSectionStates(2)}>How to Write a Good Prompt</h3>
                <hr />
                <Collapse in={openSections[2]}>
                    <div>
                        <p>Writing a good prompt is the first step in having a beneficial interaction with the AI Tutor. Here are some tips to help you write a good prompt:</p>
                        <ul>
                            <li><strong>Make sure to use clear and concise instructions.</strong> For example, instead of saying "Tell me about derivatives," try saying "Tell me the steps I need to take to calculate a derivative using the chain rule."</li>
                            <li><strong>Give details to provide direction.</strong> For example, instead of saying "Help me study for my calculus quiz," try saying "I'm taking Calculus 1, and I need to prepare for a quiz on how to calculate derivatives using the chain rule. Walk me through some practice problems that use the chain rule to calculate a derivative."</li>
                            <li><strong>Act like you are explaining something to a person.</strong> If you give a person instructions that are too long, then they could lose track of what needs to be done. If you give a person instructions with too little detail, then they could misunderstand and not do what you need.</li>
                            <li><strong>Provide specific requests.</strong> For example, "Show me a step-by-step example of how to take a derivative using the chain rule."</li>
                        </ul>
                    </div>
                </Collapse>
                <h3 className="h3-help" onClick={() => changeSectionStates(3)}>Common AI Tutor Responses</h3>
                <hr />
                <Collapse in={openSections[3]}>
                    <div>
                        <p>The AI Tutor is designed to answer prompts related to Calculus, Chemistry, or Physics in a way that does not give you the direct answer and instead helps you work through the problem on your own. This design is meant to improve your learning while avoiding an academic integrity violation. As a result, the AI Tutor may at times provide a response that does not answer your prompt. Here is a list of some of the responses you may receive, what they mean, and how you can change your prompt to get a better response:</p>
                        <ul>
                            <li><strong>Please ask a question related to Calculus, Chemistry, or Physics.</strong> The AI Tutor is designed to only respond to prompts related Calculus, Chemistry, or Physics. If you receive this response, it is likely because your prompt is not related to one of these topics. Try to ask a more specific question related to one of these subjects to get a better response from the AI Tutor.</li>
                        </ul>
                    </div>
                </Collapse>
                <h3 className="h3-help" onClick={() => changeSectionStates(4)}>FAQs</h3>
                <hr />
                <Collapse in={openSections[4]}>
                    <div>
                        <p><strong>Can I see my chat history for a class that I took during a different semester?</strong></p>
                        <Container>
                            <Container>
                                <Container>
                                    <p>Yes, you can view your chat history for any class by selecting it in the sidebar on the Chat page.</p>
                                </Container>
                            </Container>
                        </Container>
                        <p><strong>Is there a way to delete my chats with the AI Tutor?</strong></p>
                        <Container>
                            <Container>
                                <Container>
                                    <p>No, you are not able to delete any of your chats with the AI Tutor.</p>
                                </Container>
                            </Container>
                        </Container>
                        <p><strong>I noticed something that isn't working properly on the AI Tutor website. Is there a way to report this?</strong></p>
                        <Container>
                            <Container>
                                <Container>
                                    <p>Yes, you can report any feedback you have about the AI Tutor website on the Give Feedback page.</p>
                                </Container>
                            </Container>
                        </Container>
                        <p><strong>Do I have to get permission from all of my professors to use the AI Tutor website?</strong></p>
                        <Container>
                            <Container>
                                <Container>
                                    <p>Yes, it is against Miami's academic integrity policy to use the AI Tutor without permission from your professor. You should get permission from the professor of each class that you want to use the AI Tutor for.</p>
                                </Container>
                            </Container>
                        </Container>
                        <p><strong>What am I allowed to use the AI Tutor for?</strong></p>
                        <Container>
                            <Container>
                                <Container>
                                    <p>The AI Tutor is meant to provide you with an on-demand tutoring tool that will help you with understanding concepts, working through practice problems, and facilitating long-term learning in the subjects of Calculus, Chemistry, and Physics. You should use the AI Tutor to ask for help understanding concepts or working through practice problems. You should not use the AI Tutor to ask for answers to assignments or assessments. For more questions about how to use the AI Tutor, please review the Academic Integrity section above.</p>
                                </Container>
                            </Container>
                        </Container>
                    </div>
                </Collapse>
            </Container>
            </div>
        </div>
    );
}

export default HelpPage;
